import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teamApi, rolesApi } from '../api/teamApi';
import { useToast } from '../../../shared/components/toast/useToast';
import type { CreateTeamMemberPayload, UpdateTeamMemberPayload } from '../types';

const errMsg = (err: unknown, fallback: string) => (err as Error)?.message || fallback;

export const useTeam = () =>
  useQuery({ queryKey: ['merchant-team'], queryFn: teamApi.list, staleTime: 2 * 60 * 1000 });

export const useRoles = () =>
  useQuery({ queryKey: ['roles'], queryFn: rolesApi.list, staleTime: 10 * 60 * 1000 });

export const useCreateTeamMember = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreateTeamMemberPayload) => teamApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-team'] });
      toast.success('Team member added');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to add team member')),
  });
};

export const useUpdateTeamMember = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTeamMemberPayload }) => teamApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-team'] });
      toast.success('Team member updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update team member')),
  });
};

export const useDeleteTeamMember = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => teamApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-team'] });
      toast.success('Team member removed');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to remove team member')),
  });
};
